import { useSignal } from "@preact/signals";
import { navigateTo } from "bunfx";
import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { rpc } from "../../rpc";
import { currentUser } from "../../stores/current-user";
import { showError } from "../../stores/errors";
import type { BrandColor, OrganizationListItem } from "../../types";
import {
  IcoCalendar,
  IcoCheckCircle,
  IcoChevronDown,
  IcoCog,
  IcoFileText,
  IcoHandRaised,
  IcoHome,
  IcoImage,
  IcoPlus,
  IcoSignOut,
  IcoStar,
  IcoUser,
  IcoUsers,
} from "../icons";
import { NotificationBell } from "./notification-bell";

export type BrandedHeaderTab =
  | "home"
  | "check-ins"
  | "team"
  | "high-fives"
  | "values"
  | "org-chart"
  | "forms"
  | "settings";

type BrandedHeaderProps = {
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  activeTab: BrandedHeaderTab;
  isAdmin: boolean;
  /** Organizations for workspace switcher */
  organizations?: OrganizationListItem[];
  /** Form count for tab badge */
  formCount?: number;
  /** Whether the current user has direct reports (controls Team tab visibility) */
  hasReports?: boolean;
};

/**
 * Branded workspace header with two-row navigation.
 * Uses the workspace's brand color for a distinctive visual identity.
 */
export function BrandedHeader({
  orgId,
  orgName,
  brandColor,
  activeTab,
  isAdmin,
  organizations = [],
  formCount,
  hasReports,
}: BrandedHeaderProps) {
  const showSwitcher = organizations.length > 1;

  return (
    <header
      data-brand={brandColor}
      class="bg-gradient-to-r from-[oklch(0.35_0.18_250)] to-[oklch(0.50_0.20_250)]"
    >
      {/* Row 1: Logo, workspace name, notifications, user */}
      <div class="px-4 lg:px-8">
        <div class="flex h-16 items-center justify-between">
          {/* Left: Pulse icon + Workspace Name */}
          <div class="flex items-center gap-3">
            {/* Pulse line icon */}
            <svg
              class="w-8 h-5 flex-shrink-0"
              viewBox="0 0 24 12"
              fill="none"
              stroke="white"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              role="img"
              aria-label="Pulse logo"
            >
              <polyline points="1,6 4,6 6,2 9,10 12,1 15,6 18,6 20,4 23,6" />
            </svg>

            {showSwitcher ? (
              <WorkspaceSwitcher
                organizations={organizations}
                currentOrgId={orgId}
                currentOrgName={orgName}
              />
            ) : (
              <a href={`/orgs/${orgId}`} class="flex flex-col">
                <span class="text-lg font-bold text-white leading-tight">
                  {orgName}
                </span>
                <span class="text-sm text-white/70">Pulse</span>
              </a>
            )}
          </div>

          {/* Right: Notifications + User menu */}
          <div class="flex items-center gap-2 text-white">
            <div class="[&_svg]:text-white [&_button]:hover:bg-white/10">
              <NotificationBell />
            </div>
            <UserMenuDropdown orgId={orgId} />
          </div>
        </div>
      </div>

      {/* Row 2: Navigation tabs */}
      <nav class="px-4 lg:px-8 overflow-x-auto scrollbar-hide">
        <div class="flex items-center gap-1 min-w-max">
          <NavTab href={`/orgs/${orgId}`} active={activeTab === "home"}>
            <IcoHome class="size-4" />
            Home
          </NavTab>
          <NavTab
            href={`/orgs/${orgId}/check-ins`}
            active={activeTab === "check-ins"}
          >
            <IcoCheckCircle class="size-4" />
            Check-ins
          </NavTab>
          {hasReports !== false && (
            <NavTab href={`/orgs/${orgId}/team`} active={activeTab === "team"}>
              <IcoUsers class="size-4" />
              Team
            </NavTab>
          )}
          <NavTab
            href={`/orgs/${orgId}/high-fives`}
            active={activeTab === "high-fives"}
          >
            <IcoHandRaised class="size-4" />
            High Fives
          </NavTab>
          <NavTab
            href={`/orgs/${orgId}/values`}
            active={activeTab === "values"}
          >
            <IcoStar class="size-4" />
            Values
          </NavTab>
          <NavTab
            href={`/orgs/${orgId}/org-chart`}
            active={activeTab === "org-chart"}
          >
            <IcoUsers class="size-4" />
            People
          </NavTab>
          {isAdmin && (
            <>
              <NavTab
                href={`/orgs/${orgId}/forms`}
                active={activeTab === "forms"}
                count={formCount}
              >
                <IcoFileText class="size-4" />
                Forms
              </NavTab>
              <NavTab
                href={`/orgs/${orgId}/settings`}
                active={activeTab === "settings"}
              >
                <IcoCog class="size-4" />
                Settings
              </NavTab>
            </>
          )}
        </div>
      </nav>

      {/* Bottom padding to create space for overlap */}
      <div class="h-16" />
    </header>
  );
}

/**
 * Navigation tab for the branded header.
 */
function NavTab({
  href,
  active,
  children,
  count,
}: {
  href: string;
  active: boolean;
  children: ComponentChildren;
  count?: number;
}) {
  return (
    <a
      href={href}
      class={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? "text-white border-b-2 border-white"
          : "text-white/70 hover:text-white border-b-2 border-transparent"
      }`}
    >
      {children}
      {count !== undefined && (
        <span class={`text-xs ${active ? "text-white/70" : "text-white/50"}`}>
          ({count})
        </span>
      )}
    </a>
  );
}

/**
 * Workspace switcher dropdown.
 */
function WorkspaceSwitcher({
  organizations,
  currentOrgId,
  currentOrgName,
}: {
  organizations: OrganizationListItem[];
  currentOrgId: string;
  currentOrgName: string;
}) {
  const isOpen = useSignal(false);

  useEffect(() => {
    if (!isOpen.value) return;

    const onDocClick = (e: MouseEvent) => {
      const target = (e.target as Element).closest("a,[role=menu]");
      if (!target || target.matches("a")) {
        isOpen.value = false;
      }
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [isOpen.value]);

  // Group by tenant
  const groupedByTenant = organizations.reduce(
    (acc, org) => {
      const key = org.tenantId;
      if (!acc[key]) {
        acc[key] = { tenantName: org.tenantName, workspaces: [] };
      }
      acc[key].workspaces.push(org);
      return acc;
    },
    {} as Record<
      string,
      { tenantName: string; workspaces: OrganizationListItem[] }
    >,
  );

  const tenantGroups = Object.entries(groupedByTenant);
  const hasMultipleTenants = tenantGroups.length > 1;

  return (
    <div class="relative">
      <button
        type="button"
        onClick={() => (isOpen.value = !isOpen.value)}
        aria-label="Switch workspace"
        aria-expanded={isOpen.value}
        class="flex items-center gap-2 hover:opacity-90 transition-opacity"
      >
        <div class="flex flex-col text-left">
          <span class="text-lg font-bold text-white leading-tight">
            {currentOrgName}
          </span>
          <span class="text-sm text-white/70">Pulse</span>
        </div>
        <IcoChevronDown class="size-4 text-white/70" />
      </button>

      {isOpen.value && (
        <div
          role="menu"
          class="absolute left-0 top-full mt-2 z-50 w-72 bg-surface border border-border rounded-xl shadow-lg overflow-hidden"
        >
          {tenantGroups.map(([tenantId, { tenantName, workspaces }]) => (
            <div key={tenantId}>
              {hasMultipleTenants && (
                <div class="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider bg-surface-muted border-b border-border">
                  {tenantName}
                </div>
              )}
              {workspaces.map((org) => (
                <a
                  key={org.id}
                  href={`/orgs/${org.id}`}
                  class={`flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors ${
                    org.id === currentOrgId ? "bg-surface-selected" : ""
                  }`}
                >
                  <div class="size-8 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                    {org.logoUrl ? (
                      <img
                        src={org.logoUrl}
                        alt=""
                        class="size-full object-cover"
                      />
                    ) : (
                      <IcoImage class="size-4 text-text-muted" />
                    )}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-text truncate">{org.name}</div>
                    <div class="text-sm text-text-muted">
                      {org.memberCount}{" "}
                      {org.memberCount === 1 ? "member" : "members"}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ))}

          <div class="p-2 border-t border-border">
            <a
              href="/orgs/new"
              class="inline-flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-lg bg-surface-hover hover:bg-surface-active text-text-secondary transition-colors"
            >
              <IcoPlus class="size-4" />
              New Workspace
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * User avatar dropdown menu.
 */
function UserMenuDropdown({ orgId }: { orgId: string }) {
  const user = currentUser.value;
  const menuOpen = useSignal(false);

  async function handleLogout() {
    try {
      await rpc.auth.logout({});
      navigateTo("/login");
    } catch {
      showError("Logout failed", "Please try again.");
    }
  }

  return (
    <div class="relative">
      <button
        type="button"
        onClick={() => (menuOpen.value = !menuOpen.value)}
        class="flex items-center gap-2 rounded-full p-1 hover:bg-white/10 transition-colors"
        aria-expanded={menuOpen.value}
        aria-haspopup="menu"
      >
        <div class="size-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
          {user?.profilePictureUrl ? (
            <img
              src={user.profilePictureUrl}
              alt=""
              class="size-full object-cover"
            />
          ) : (
            <IcoUser class="size-5 text-white" />
          )}
        </div>
      </button>

      {menuOpen.value && (
        <>
          <div
            class="fixed inset-0 z-40"
            onClick={() => (menuOpen.value = false)}
          />
          <div
            class="absolute right-0 top-full mt-2 z-50 w-56 rounded-xl bg-surface p-1 shadow-lg ring-1 ring-border"
            role="menu"
          >
            <div class="flex items-center gap-3 px-3 py-2 border-b border-border mb-1">
              <div class="size-8 rounded-full bg-surface-muted flex items-center justify-center overflow-hidden">
                {user?.profilePictureUrl ? (
                  <img
                    src={user.profilePictureUrl}
                    alt=""
                    class="size-full object-cover"
                  />
                ) : (
                  <IcoUser class="size-5 text-text-muted" />
                )}
              </div>
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-medium text-text truncate">
                  {user?.name}
                </span>
                <span class="text-xs text-text-muted truncate">
                  {user?.email}
                </span>
              </div>
            </div>
            <a
              href="/account"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-text hover:bg-surface-hover"
              role="menuitem"
              onClick={() => (menuOpen.value = false)}
            >
              <IcoUser class="size-4 text-text-muted" />
              Account
            </a>
            <a
              href={`/orgs/${orgId}/time-off`}
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-text hover:bg-surface-hover"
              role="menuitem"
              onClick={() => (menuOpen.value = false)}
            >
              <IcoCalendar class="size-4 text-text-muted" />
              Time Off
            </a>
            <div class="border-t border-border-muted my-1" />
            <button
              type="button"
              onClick={handleLogout}
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-text hover:bg-surface-hover"
              role="menuitem"
            >
              <IcoSignOut class="size-4 text-text-muted" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
